import { motion, AnimatePresence } from 'framer-motion';
import { Shield, CheckCircle, ArrowRight, Search, FileSearch, Award, Clock, Star, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useContactPopup } from '@/hooks/useContactPopup';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';

const ServiceForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+44',
    phone: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const submissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
    submissions.push({ ...formData, timestamp: new Date().toISOString(), source: 'search-service-page' });
    localStorage.setItem('contactSubmissions', JSON.stringify(submissions));
    toast.success('Thank you! We\'ll contact you within 24 hours with your comprehensive trademark search.');
    setFormData({ name: '', email: '', countryCode: '+44', phone: '', message: '' });
  };

  const countryCodes = [
    { code: '+44', country: 'UK' },
    { code: '+1', country: 'US/Canada' },
    { code: '+91', country: 'India' },
    { code: '+61', country: 'Australia' },
    { code: '+353', country: 'Ireland' }
  ];

  return (
    <Card className="p-8 border-2 border-primary shadow-elegant-xl">
      <div className="mb-6">
        <Badge className="mb-3 bg-secondary text-secondary-foreground">Special Offer</Badge>
        <h3 className="text-2xl font-bold text-foreground mb-2">Get Your Trademark Search - Only £150</h3>
        <p className="text-muted-foreground">Comprehensive trademark search with detailed conflict analysis. Fill out the form and we'll contact you within 24 hours.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="service-name">Name *</Label>
          <Input
            id="service-name"
            placeholder="John Smith"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="service-email">Email *</Label>
          <Input
            id="service-email"
            type="email"
            placeholder="john@example.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="service-phone">Contact Number *</Label>
          <div className="flex gap-2">
            <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value })}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {countryCodes.map((item) => (
                  <SelectItem key={item.code} value={item.code}>
                    {item.code} ({item.country})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="service-phone"
              type="tel"
              placeholder="1234567890"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              className="flex-1"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="service-message">Trademark Name/Details</Label>
          <Textarea
            id="service-message"
            placeholder="Tell us about the trademark you want to search (name, logo description, intended use)"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            rows={4}
          />
        </div>

        <Button type="submit" className="w-full bg-gradient-to-r from-primary to-secondary hover:shadow-glow transition-all duration-300">
          Get My Trademark Search Report
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </form>
    </Card>
  );
};

const TestimonialCard = ({ testimonial }) => (
  <Card className="p-6 h-full flex flex-col border-border shadow-lg hover:shadow-xl transition-shadow duration-300">
    <div className="flex gap-1 mb-4">
      {[1, 2, 3, 4, 5].map((star) => {
        const rating = testimonial.rating;
        const isFullStar = star <= rating;
        const isHalfStar = star === Math.ceil(rating) && rating % 1 !== 0;
        
        return (
          <div key={star} className="relative w-5 h-5">
            {isHalfStar ? (
              <>
                <Star className="w-5 h-5 text-gray-300 absolute inset-0" fill="currentColor" />
                <div className="overflow-hidden absolute w-2.5 h-5">
                  <Star className="w-5 h-5 text-yellow-400" fill="currentColor" />
                </div>
              </>
            ) : (
              <Star 
                className={`w-5 h-5 ${isFullStar ? 'text-yellow-400' : 'text-gray-300'}`} 
                fill="currentColor" 
              />
            )}
          </div>
        );
      })}
    </div>
    
    <p className="text-base text-foreground mb-6 leading-relaxed italic flex-1">
      &quot;{testimonial.content}&quot;
    </p>
    
    <div className="border-t border-border pt-4">
      <p className="font-semibold text-foreground">{testimonial.name}</p>
      <p className="text-sm text-muted-foreground mt-1">{testimonial.role}</p>
    </div>
  </Card>
);

export const ComprehensiveSearchPage = () => {
  const { openPopup } = useContactPopup();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      name: 'Priya Sharma',
      role: 'Marketing Director, GreenLeaf Organics',
      content: 'Before launching our rebrand, we needed certainty that our new name was available. Trademark Assist\'s comprehensive search uncovered a potential conflict we\'d completely missed. They saved us from a costly legal nightmare and helped us pivot to a stronger brand name.',
      rating: 5
    },
    {
      name: 'Sarah Mitchell',
      role: 'Founder & CEO, TechFlow Solutions',
      content: 'The detailed search report gave us complete confidence before filing our trademark application. They found similar marks we never would have discovered on our own and explained the risk level for each one. Worth every penny.',
      rating: 5
    },
    {
      name: 'Oliver Martinez',
      role: 'Brand Manager, Luxury Watches UK',
      content: 'We thought our brand name was unique until Trademark Assist conducted their search. They identified three potential conflicts and provided strategic advice on how to modify our mark to avoid issues. Their expertise saved us months of delays.',
      rating: 4.5
    },
    {
      name: 'Michael Chen',
      role: 'Co-Founder, UrbanBuild Construction Ltd',
      content: 'The search report was incredibly thorough and easy to understand. They didn\'t just list similar trademarks—they explained why each one might be a problem and what our options were. This level of detail gave us the confidence to proceed.',
      rating: 5
    },
    {
      name: 'Emma Thompson',
      role: 'E-commerce Manager, Heritage Home Goods',
      content: 'Getting a comprehensive search before filing was the best decision we made. Trademark Assist found a similar mark that could have resulted in opposition. We adjusted our brand name slightly and now have a registered trademark with zero conflicts.',
      rating: 5
    }
  ];

  const getVisibleTestimonials = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const testimonialsPerView = isMobile ? 1 : 3;
    const visible = [];
    for (let i = 0; i < testimonialsPerView; i++) {
      const index = (currentTestimonial + i) % testimonials.length;
      visible.push({ ...testimonials[index], originalIndex: index });
    }
    return visible;
  };

  const nextTestimonial = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const step = isMobile ? 1 : 3;
    setCurrentTestimonial((prev) => (prev + step) % testimonials.length);
  };

  const prevTestimonial = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const step = isMobile ? 1 : 3;
    setCurrentTestimonial((prev) => (prev - step + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      nextTestimonial();
    }, 6000);
    return () => clearInterval(testimonialInterval);
  }, [currentTestimonial]);

  const steps = [
    {
      number: '01',
      title: 'Trademark Details Collection',
      description: 'We gather comprehensive information about your proposed trademark—the name, logo, intended goods or services, and target market. The more details you provide, the more accurate and valuable our search becomes.'
    },
    {
      number: '02',
      title: 'Multi-Database Search & Analysis',
      description: 'Our experts search the UK IPO database, Companies House records, domain registrations, and common law trademarks. We identify identical and similar marks, analyzing phonetic similarities, visual resemblances, and conceptual connections.'
    },
    {
      number: '03',
      title: 'Detailed Report & Recommendations',
      description: 'You receive a comprehensive report categorizing all potential conflicts by risk level (high, medium, low), with visual examples and strategic recommendations on whether to proceed, modify your mark, or choose an alternative approach.'
    }
  ];

  const usps = [
    {
      icon: <Search className="w-8 h-8" />,
      title: 'Beyond Basic Database Searches',
      description: 'We don\'t just search the UK IPO register. Our comprehensive search includes Companies House records, domain name registrations, social media handles, common law trademarks, and phonetically similar marks. This depth is why we catch conflicts that basic searches miss—and why our clients achieve a 98% approval rate on subsequent applications.'
    },
    {
      icon: <AlertCircle className="w-8 h-8" />,
      title: 'Risk Assessment Expertise',
      description: 'Finding similar marks is just the beginning. Our experts analyze each potential conflict to assess the real risk level. Not every similar mark poses a problem—much depends on industry, goods/services, and visual/phonetic differences. We provide clear, strategic guidance on which conflicts matter and which don\'t.'
    },
    {
      icon: <FileSearch className="w-8 h-8" />,
      title: 'Detailed Visual Reports',
      description: 'Our search reports include visual examples of similar marks, clear explanations of why they might conflict, and specific recommendations tailored to your situation. You won\'t receive a confusing list of results—you\'ll get actionable insights you can actually use to make informed decisions about your brand.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Prevent Costly Rejections',
      description: 'Over 40% of trademark applications are rejected due to conflicts with existing marks. Our comprehensive search identifies these issues before you file, saving you the £200+ IPO filing fee, months of waiting, and the frustration of starting over. Prevention is always more cost-effective than dealing with rejections or oppositions later.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Badge className="mb-4 bg-primary text-primary-foreground">Professional Trademark Search Service</Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
                Comprehensive Trademark Search Before You File
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Don't risk your trademark application on uncertainty. Our comprehensive trademark search identifies potential conflicts before you file, saving you time, money, and frustration. We search beyond the UK IPO database, analyzing Companies House records, domain registrations, and common law trademarks to give you complete confidence. With detailed risk assessments and strategic recommendations, our £150 search service is the smartest investment you'll make before filing. Over 40% of applications are rejected due to conflicts—let us help you avoid that fate.
              </p>
              
              {/* CTA Button */}
              <div className="flex gap-4">
                <Button 
                  onClick={openPopup}
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground group"
                >
                  Order Your Search
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <ServiceForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Service */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <img src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop" alt="Trademark Search" className="rounded-lg shadow-elegant-xl w-full h-auto" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">What Is a Comprehensive Trademark Search?</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                A comprehensive trademark search is a thorough investigation to determine whether your proposed trademark conflicts with existing marks. Unlike basic database searches, our service examines multiple sources including the UK IPO register, Companies House records, domain name databases, and common law trademarks. We analyze not just identical matches but also phonetically similar names, visually similar logos, and conceptually related marks. Our experts assess each potential conflict based on the specific goods or services you plan to offer, providing a detailed risk analysis. This comprehensive approach is why our clients achieve a 98% trademark approval rate—we identify and address conflicts before filing, not after rejection. The £150 investment in a proper search can save you thousands in rejected applications, rebranding costs, and legal disputes.
              </p>
              
              {/* CTA Button */}
              <div className="mt-8">
                <Button 
                  onClick={openPopup}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground group"
                  size="lg"
                >
                  Order Your Search
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 px-4 bg-gradient-to-br from-secondary/5 to-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">How We Work: Our Trademark Search Process</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Our proven three-step process delivers the most comprehensive trademark search in the UK. We combine advanced database searches with expert human analysis, ensuring nothing is missed. From collection to final report, expect results within 3-5 business days.
              </p>
            </motion.div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                <Card className="p-8 h-full border-2 border-transparent hover:border-primary transition-all duration-300 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 text-8xl font-bold text-primary/5 group-hover:text-primary/10 transition-colors duration-300">{step.number}</div>
                  <div className="relative z-10">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center text-primary-foreground font-bold text-2xl mb-6 shadow-lg">{step.number}</div>
                    <h3 className="text-xl font-semibold text-foreground mb-4">{step.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
          
          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button 
              onClick={openPopup}
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground group"
              size="lg"
            >
              Get Started Now
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Badge className="mb-4 bg-secondary/10 text-secondary border-secondary/20">Transparent Pricing</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Simple Pricing for Trademark Searches</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">Choose the search depth that matches your needs and risk tolerance.</p>
            </motion.div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <Card className="p-8 h-full flex flex-col border-2 border-border hover:border-primary/30 hover:shadow-elegant-xl transition-all duration-300 relative overflow-hidden group bg-gradient-to-br from-card to-background">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-foreground mb-2">UK Expert Search Report</h3>
                  <div className="mb-6"><div className="text-4xl font-bold text-primary mb-2">from £230</div><p className="text-sm text-muted-foreground">one-time fee</p></div>
                  <p className="text-muted-foreground mb-6">Expert UK database search with detailed analysis.</p>
                  <div className="space-y-3 mb-8">
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">UK IPO database search</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Identical & similar mark identification</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Single class search</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Expert conflict report</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">3-5 day turnaround</span></div>
                  </div>
                  <Button onClick={openPopup} className="w-full mt-auto bg-gradient-to-r from-primary/80 to-secondary/80 hover:from-primary hover:to-secondary transition-all duration-300 relative z-10">Choose UK Expert</Button>
                </div>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <Card className="p-8 h-full flex flex-col border-2 border-primary hover:shadow-elegant-2xl transition-all duration-300 relative overflow-hidden group bg-gradient-to-br from-primary/5 to-background shadow-elegant-xl scale-105">
                <Badge className="absolute top-4 right-4 bg-secondary text-secondary-foreground">Most Popular</Badge>
                <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full -mr-20 -mt-20 group-hover:scale-150 transition-transform duration-500"></div>
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-foreground mb-2">Full Clearance Search & Report UK/EU</h3>
                  <div className="mb-6"><div className="text-4xl font-bold text-primary mb-2">from £350</div><p className="text-sm text-muted-foreground">one-time fee</p></div>
                  <p className="text-muted-foreground mb-6">Comprehensive UK & EU search with expert risk analysis.</p>
                  <div className="space-y-3 mb-8">
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">UK & EU database search</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Multi-database (IPO, Companies House)</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Up to 3 classes search</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Detailed risk assessment</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Strategic recommendations</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">2-3 day turnaround</span></div>
                  </div>
                  <Button onClick={openPopup} className="w-full mt-auto bg-gradient-to-r from-primary to-secondary hover:shadow-glow transition-all duration-300 relative z-10">Choose Full Clearance</Button>
                </div>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
              <Card className="p-8 h-full flex flex-col border-2 border-secondary hover:border-secondary hover:shadow-elegant-xl transition-all duration-300 relative overflow-hidden group bg-gradient-to-br from-secondary/5 to-background">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-secondary/10 to-primary/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-foreground mb-2">Comprehensive Global Search</h3>
                  <div className="mb-6"><div className="text-4xl font-bold text-secondary mb-2">from £499</div><p className="text-sm text-muted-foreground">one-time fee</p></div>
                  <p className="text-muted-foreground mb-6">Global trademark search for international expansion.</p>
                  <div className="space-y-3 mb-8">
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">UK + EU + International search</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Global database search</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Unlimited classes</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Comprehensive risk analysis</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Multi-jurisdiction strategy</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">Expert consultation call</span></div>
                    <div className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" /><span className="text-sm text-muted-foreground">1-2 day priority turnaround</span></div>
                  </div>
                  <Button onClick={openPopup} className="w-full mt-auto bg-gradient-to-r from-primary to-secondary hover:shadow-glow transition-all duration-300 relative z-10">Choose Global</Button>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">Why Choose Trademark Assist for Your Search</h2>
              <p className="text-lg text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                With 10,000+ searches conducted and a 98% approval rate, we deliver expert analysis and actionable intelligence—not just raw data—to help you file with confidence.
              </p>
            </motion.div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {usps.map((usp, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                <Card className="p-8 border-2 border-border hover:border-primary/50 transition-all duration-500 h-full group relative overflow-hidden bg-white hover:bg-gradient-to-br hover:from-primary/5 hover:to-secondary/5 hover:shadow-[0_20px_60px_-15px_rgba(1,33,105,0.3)] hover:scale-[1.02]">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"></div>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 -mr-16 -mt-16"></div>
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-secondary/20 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 -ml-16 -mb-16"></div>
                  <div className="relative z-10">
                    <div className="relative w-20 h-20 mb-6 group-hover:mb-8 transition-all duration-500">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"></div>
                      <div className="relative w-20 h-20 bg-gradient-to-br from-primary/10 to-secondary/10 group-hover:from-primary group-hover:to-secondary rounded-2xl flex items-center justify-center text-primary group-hover:text-white transition-all duration-500 group-hover:rotate-[360deg] group-hover:scale-110 shadow-lg group-hover:shadow-2xl">
                        {usp.icon}
                      </div>
                    </div>
                    <h3 className="text-2xl font-semibold text-foreground mb-4 group-hover:text-primary transition-all duration-500 group-hover:tracking-wide">{usp.title}</h3>
                    <p className="text-muted-foreground leading-relaxed group-hover:text-foreground/90 transition-all duration-500">{usp.description}</p>
                    <div className="mt-6 h-1 w-0 group-hover:w-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-700 ease-out"></div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1920&h=600&fit=crop" alt="Trademark Search" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary-dark/90 to-secondary/85"></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-primary-foreground">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Get Your Comprehensive Trademark Search Today - From £230</h2>
            <p className="text-lg mb-8 text-primary-foreground/90 leading-relaxed max-w-3xl mx-auto">
              Don't risk rejection and wasted fees by filing blind. Our comprehensive trademark search identifies potential conflicts before you invest in a full application. With expert analysis, clear risk assessments, and strategic recommendations, you'll know exactly where you stand. Join the 98% of our search clients who successfully register their trademarks on the first try.
            </p>
            <Button onClick={openPopup} size="lg" variant="secondary" className="text-lg px-8 bg-primary-foreground text-primary hover:bg-primary-foreground/90 hover:scale-105 transition-all duration-300 shadow-elegant-xl">
              Get My Trademark Search Report
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-gradient-to-br from-secondary/5 to-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16"><h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">What Our Clients Say About Our Search Service</h2></div>
          <div className="relative">
            <button onClick={prevTestimonial} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-10 bg-primary text-primary-foreground rounded-full p-3 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300" aria-label="Previous testimonials"><ChevronLeft className="w-6 h-6" /></button>
            <button onClick={nextTestimonial} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 z-10 bg-primary text-primary-foreground rounded-full p-3 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300" aria-label="Next testimonials"><ChevronRight className="w-6 h-6" /></button>
            <div className="overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div key={currentTestimonial} initial={{ opacity: 0, x: 100 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -100 }} transition={{ duration: 0.5, ease: "easeInOut" }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {getVisibleTestimonials().map((testimonial, index) => (<div key={`${testimonial.originalIndex}-${index}`}><TestimonialCard testimonial={testimonial} /></div>))}
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, index) => (<button key={index} onClick={() => setCurrentTestimonial(index * 3)} className={`h-2 rounded-full transition-all duration-500 ease-out ${Math.floor(currentTestimonial / 3) === index ? 'bg-primary w-8 shadow-glow' : 'bg-border w-2 hover:bg-primary/50 hover:w-4'}`} aria-label={`View testimonial group ${index + 1}`} />))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16"><h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Frequently Asked Questions About Trademark Searches</h2></div>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">Why do I need a trademark search if the UK IPO will search when I apply?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">The UK IPO does conduct a search, but by then you've already paid the non-refundable filing fee (£200+ per class) and invested time waiting for examination. If conflicts are found, your application is refused and you've lost that fee entirely. Our pre-filing search costs less than the IPO fee and identifies problems before you invest. Plus, we provide strategic guidance the IPO can't offer—we'll suggest modifications or alternatives that could work. Prevention is always cheaper than dealing with rejection.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">What makes your search &quot;comprehensive&quot; compared to free searches?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">Free searches typically only check for identical matches in the UK IPO database. Our comprehensive search goes much further: we search for similar (not just identical) marks, check Companies House records for company names that might conflict, review domain registrations, look for common law trademarks (unregistered marks that still have rights), and analyze phonetic similarities and conceptual connections. Most importantly, we provide expert human analysis of results—not just a list of matches, but a strategic assessment of which conflicts truly matter and what you should do about them.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">How long does a trademark search take?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">Our turnaround times depend on the package: Basic Search (3-5 business days), Comprehensive Search (2-3 business days), and International Search (1-2 business days with priority handling). These timeframes begin once we've received all necessary information about your proposed mark. The actual search and analysis work is thorough—we don't rush quality—but we've streamlined our process to deliver results quickly. If you have an urgent need, our International package includes priority processing, and we can sometimes accommodate rush requests for an additional fee.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">What if the search finds conflicts? Can I still register my trademark?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">Finding potential conflicts doesn't necessarily mean you can't proceed—it depends on the nature and severity of the conflicts. Our report categorizes conflicts by risk level (high, medium, low) and explains why each one might be a problem. High-risk conflicts usually require trademark modifications or a different approach. Medium-risk conflicts might be acceptable depending on factors like different goods/services or visual differences. Low-risk conflicts often aren't real obstacles. We provide strategic recommendations for each scenario, including suggestions for modifications that could work. Many clients successfully register after making small adjustments based on our search findings.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-5" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">Do I need a separate search for each class of goods/services?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">Our Basic Search covers one class, Comprehensive Search covers up to three classes, and International Search includes unlimited classes. If you're planning to register in multiple classes (which is common for businesses offering different types of products or services), it's more efficient and cost-effective to search all relevant classes at once. The cost per additional class decreases with our Comprehensive package. We'll help you identify which classes are relevant during our initial consultation—many businesses discover they need more (or fewer) classes than they originally thought once we discuss their actual and planned business activities.</AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default ComprehensiveSearchPage;
